export interface PromptExample {
  name: string;
  description: string;
  prompt: string;
}

export const summaryExamples: PromptExample[] = [
  {
    name: "Concise Bullet Points",
    description: "Brief, scannable summary with key points",
    prompt: `Create a concise summary of this call in bullet points. Include:
• Key discussion topics
• Customer pain points mentioned
• Next steps or action items
• Overall call outcome

Keep it brief and easy to scan.`
  },
  {
    name: "Sales-Focused Summary",
    description: "Emphasizes qualification and opportunity details",
    prompt: `Summarize this sales call with focus on:

QUALIFICATION:
- Budget/Authority/Need/Timeline (BANT)
- Decision makers involved
- Current solution/competitor

OPPORTUNITY:
- Key pain points and their impact
- Products/services discussed
- Potential deal size or value

NEXT STEPS:
- Agreed action items with owners
- Follow-up timeline`
  },
  {
    name: "Problem-Solution Format",
    description: "Focuses on customer problems and proposed solutions",
    prompt: `Provide a problem-solution summary:

CHALLENGES DISCUSSED:
List the specific problems or challenges the prospect mentioned.

SOLUTIONS PROPOSED:
Describe how our product/service addresses each challenge.

CUSTOMER REACTION:
Note their level of interest and any objections raised.

FOLLOW-UP PLAN:
Detail the next steps agreed upon.`
  },
  {
    name: "Executive Summary",
    description: "High-level overview for leadership",
    prompt: `Create an executive summary suitable for leadership review:

OVERVIEW: One-sentence summary of the call purpose and outcome.

KEY INSIGHTS: 2-3 most important takeaways about the prospect, their needs, and opportunity potential.

BUSINESS IMPACT: How this opportunity aligns with company priorities and revenue goals.

RECOMMENDED ACTIONS: Critical next steps to move this forward.`
  },
  {
    name: "Discovery Call Summary",
    description: "Detailed format for initial discovery calls",
    prompt: `Summarize this discovery call covering:

COMPANY BACKGROUND:
- Industry, size, and key business details
- Current tech stack or processes

PAIN POINTS:
- Primary challenges they're facing
- Impact on their business
- Urgency level

GOALS & PRIORITIES:
- What they're trying to achieve
- Timeline for implementation
- Success metrics mentioned

STAKEHOLDERS:
- Who we spoke with and their role
- Other decision makers to involve
- Buying process

QUALIFICATION:
- Budget indication
- Timeline for decision
- Competition or alternatives considered

NEXT STEPS:
- Specific actions with dates
- Resources to share
- Demo or follow-up scheduled`
  },
  {
    name: "Objection-Focused Summary",
    description: "Highlights concerns and how they were addressed",
    prompt: `Create a summary focused on objections and responses:

OBJECTIONS RAISED:
List each concern or objection the prospect mentioned.

OUR RESPONSES:
For each objection, describe how it was addressed.

UNRESOLVED CONCERNS:
Note any objections that require follow-up.

PATH FORWARD:
Next steps to overcome remaining obstacles.`
  }
];

export const emailExamples: PromptExample[] = [
  {
    name: "Professional Follow-Up",
    description: "Standard professional follow-up email",
    prompt: `Write a professional follow-up email that:
- Thanks them for their time
- Recaps the key points we discussed
- Restates their main pain points and our proposed solution
- Outlines the agreed next steps with clear action items
- Ends with a specific call-to-action

Keep the tone professional but warm. Use their name and company name appropriately.`
  },
  {
    name: "Value-Focused Email",
    description: "Emphasizes ROI and business value",
    prompt: `Compose a follow-up email that emphasizes business value:

- Brief thank you for the conversation
- Highlight the specific pain points they mentioned and their business impact
- Explain how our solution directly addresses each challenge with quantifiable benefits
- Include relevant ROI metrics or success stories from similar companies
- Clear next steps to move forward
- Strong, confident closing

Tone: consultative, results-oriented, professional`
  },
  {
    name: "Casual & Conversational",
    description: "Friendly, approachable tone",
    prompt: `Write a friendly, conversational follow-up email:

Keep it casual but professional. Reference specific moments from our conversation to make it personal. Summarize what we talked about in a natural way, not bullet points. Make the next steps feel easy and low-pressure.

Tone: warm, helpful, like you're writing to someone you've already built rapport with.`
  },
  {
    name: "Resource-Rich Email",
    description: "Includes helpful materials and references",
    prompt: `Create a follow-up email that positions us as a helpful resource:

- Thank them for the discussion
- Brief recap of their situation and goals
- Offer 2-3 specific resources that would help them (case studies, guides, etc.) based on their needs
- Suggest a specific next action
- Include a helpful tip or insight related to their challenges

Tone: educational, helpful, consultative - position yourself as a trusted advisor, not just a salesperson.`
  },
  {
    name: "Decision-Maker Email",
    description: "Formatted for busy executives",
    prompt: `Write an executive-friendly follow-up email:

Keep it BRIEF and scannable. Start with a one-sentence summary of the opportunity. Use short paragraphs and clear formatting. Focus on:
- Business impact and ROI
- Risk mitigation
- Timeline and resources required
- Specific next step

Make it easy for a busy executive to understand the value proposition in under 30 seconds.`
  },
  {
    name: "Post-Demo Email",
    description: "After product demonstration",
    prompt: `Compose a post-demo follow-up email:

- Thank them for the demo session
- Highlight the specific features that addressed their needs
- Recap any "wow moments" or positive reactions
- Address any questions or concerns raised during demo
- Provide links to relevant documentation or trial access
- Propose clear next steps (trial, POC, another meeting)

Tone: enthusiastic but not pushy, focused on helping them succeed.`
  },
  {
    name: "Re-Engagement Email",
    description: "For rekindling cold conversations",
    prompt: `Write a re-engagement email for a prospect who's gone quiet:

- Brief reference to our previous conversation
- Acknowledge the time gap without being apologetic
- Share something new or relevant (new feature, case study, market insight)
- Ask a low-commitment question to restart dialogue
- Make it easy to re-engage

Tone: professional, curious, offering value - give them a reason to respond.`
  }
];

export const researchExamples: PromptExample[] = [
  {
    name: "Comprehensive Company Research",
    description: "General purpose company research",
    prompt: `Research this company's website and provide:

COMPANY OVERVIEW:
- Core business and industry
- Key products or services
- Company size and stage (startup, growth, enterprise)
- Geographic presence

TARGET MARKET:
- Who they serve (industries, company sizes, personas)
- Market positioning and differentiators

BUSINESS MODEL:
- How they make money
- Pricing approach if visible

KEY DECISION MAKERS:
- Leadership team members
- Relevant contact information

RECENT NEWS/UPDATES:
- Latest announcements, funding, partnerships

Look for pages like: About Us, Team, Leadership, Company, Careers, Press/News, Blog`
  },
  {
    name: "Sales Qualification Research",
    description: "Focus on sales-relevant information",
    prompt: `Research this company to help qualify them as a sales opportunity:

COMPANY SIZE & GROWTH:
- Employee count estimates
- Recent growth indicators (hiring, expansion, funding)

TECH STACK:
- Technologies or tools they use (mentioned on careers, engineering blogs)
- Integration possibilities

PAIN POINTS & CHALLENGES:
- Problems they're trying to solve
- Gaps in their current solutions

BUYING SIGNALS:
- Recent funding or growth
- Job openings relevant to our solution
- Blog posts discussing related challenges

COMPETITIVE LANDSCAPE:
- Similar companies or competitors
- Market position

Prioritize pages: Careers, About, Blog, Press, Team, Product/Solutions`
  },
  {
    name: "Decision Maker Research",
    description: "Focus on finding key contacts and org structure",
    prompt: `Research this company to identify decision makers and organizational structure:

LEADERSHIP TEAM:
- C-suite executives (CEO, CTO, CFO, etc.)
- VP and Director level contacts
- Relevant department heads based on our solution

CONTACT INFORMATION:
- Names and titles
- Email addresses or contact forms
- LinkedIn profiles if linked

REPORTING STRUCTURE:
- Who reports to whom
- Size of relevant departments

DECISION-MAKING PROCESS:
- Centralized or distributed
- Key influencers for our type of solution

Focus on: Team, About, Leadership, Executive Team, Management pages`
  },
  {
    name: "Competitive Intelligence",
    description: "Understand their market position and competitors",
    prompt: `Research this company from a competitive intelligence perspective:

POSITIONING:
- How they describe themselves
- Key differentiators they claim
- Target market and ideal customer profile

PRODUCT/SERVICE OFFERINGS:
- Core offerings and features
- Pricing if available
- Recent product launches

MARKET APPROACH:
- Go-to-market strategy
- Marketing messaging and value props
- Customer testimonials or case studies

COMPANY TRACTION:
- Customer logos or client list
- Growth indicators
- Market recognition or awards

Look for: Product, Solutions, Customers, Case Studies, About, Pricing, Press pages`
  },
  {
    name: "Partnership Opportunity Research",
    description: "Evaluate potential for partnerships or integrations",
    prompt: `Research this company for partnership opportunities:

PARTNERSHIP EXISTING:
- Current partners or integrations
- Partner program information
- Technology ecosystem

INTEGRATION CAPABILITIES:
- APIs or developer resources
- Platform architecture
- Technical compatibility

STRATEGIC FIT:
- Overlapping target markets
- Complementary offerings
- Shared customer base

BUSINESS DEVELOPMENT:
- Partnership or alliance team contacts
- Business development approach
- Integration priorities

Focus on: Partners, Integrations, API, Developers, Platform, About pages`
  },
  {
    name: "Funding & Financial Research",
    description: "Research company funding and financial indicators",
    prompt: `Research this company's funding and financial health:

FUNDING HISTORY:
- Recent funding rounds
- Total capital raised
- Investors and backers

GROWTH INDICATORS:
- Expansion announcements
- New office openings
- Hiring velocity

FINANCIAL HEALTH SIGNALS:
- Revenue indicators if public
- Customer acquisition momentum
- Market validation

STRATEGIC INITIATIVES:
- Recent acquisitions or mergers
- Major partnerships announced
- Product expansion

Look for: Press, News, About, Investors, Careers pages`
  }
];

